import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

/**
 * F-241: the five faces of the mood pulse, 1 (very bad) to 5 (very good).
 * The same emojis the invitation e-mail shows (memberfy-api
 * `utils/surveyNotify.js`), so the tapped face is the one that opens marked.
 */
export const MOOD_FACES = ['\u{1F61E}', '\u{1F641}', '\u{1F610}', '\u{1F642}', '\u{1F604}'] as const
export const MOOD_VALUES = [1, 2, 3, 4, 5] as const
export const MOOD_PULSE_TEMPLATE = 'mood-pulse'

export const faceOf = (value: number): string => MOOD_FACES[Math.min(5, Math.max(1, Math.round(value))) - 1]

interface MoodFacesProps {
  value: number | null
  onChange?: (value: number) => void
  disabled?: boolean
  /** The editor's canvas: the faces drawn, not pickable. */
  readOnly?: boolean
  size?: 'md' | 'lg'
  labelledBy?: string
  className?: string
}

/** The five faces as one radio group, each with its name for screen readers and on hover. */
export const MoodFaces = ({ value, onChange, disabled = false, readOnly = false, size = 'md', labelledBy, className }: MoodFacesProps) => {
  const { t } = useTranslation('moodPulse')
  return (
    <div role={readOnly ? undefined : 'radiogroup'} aria-labelledby={labelledBy} aria-hidden={readOnly || undefined}
      className={cn('grid grid-cols-5 gap-1.5', className)}>
      {MOOD_VALUES.map((v) => {
        const active = value === v
        const common = cn(
          'flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl border px-1 transition',
          size === 'lg' ? 'py-3' : 'py-2',
          active ? 'border-primary bg-primary-50 ring-2 ring-primary/30' : 'border-border bg-surface',
          value !== null && !active && 'opacity-60'
        )
        const face = <span className={cn('leading-none', size === 'lg' ? 'text-3xl sm:text-4xl' : 'text-2xl sm:text-3xl')} aria-hidden>{MOOD_FACES[v - 1]}</span>
        const label = <span className="w-full break-words text-center text-[11px] leading-tight text-foreground-secondary sm:text-xs">{t(`faces.${v}`)}</span>
        if (readOnly) return <span key={v} className={common}>{face}{label}</span>
        return (
          <button key={v} type="button" role="radio" aria-checked={active} aria-label={t(`faces.${v}`)} title={t(`faces.${v}`)} disabled={disabled}
            onClick={() => onChange?.(v)} data-testid={`mood-face-${v}`}
            className={cn(common, 'hover:border-primary hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed')}>
            {face}{label}
          </button>
        )
      })}
    </div>
  )
}
